import { createApi } from '@reduxjs/toolkit/query/react';
import { createBackendBaseQuery, transformApiError } from '../../utils/createAuthBaseQuery';
import { API_URLS } from '../../constants/apiUrl';

export interface PatchOperation {
  operationType?: number;
  path: string;
  op: string;
  from?: string;
  value: string | number | boolean;
}

export interface ElectricalData {
  id: number;
  motorInvestigationFlowId: number;
  motorInvestigationFlowAlternativeId: number | null;
  isInvestigationInternal: boolean;
  isInvestigationInProgress: boolean;
  isOldMotorData: boolean;
  acMotorNumber: string | null;
  isActive: boolean | null;
  regionId: number | null;
  region: string | null;
  range: string | null;
  motorTypeId: number | null;
  motorType: string | null;
  make: string | null;
  supplierType: string | null;
  brand: string | null;
  frameSize: string | null;
  motorSpeedTypeId: number | null;
  motorSpeedType: string | null;
  efficiencyClass: string | null;
  operatingPoint: string | null;
  nominalVoltageV: number | null;
  frequencyHz: number | null;
  numberOfPoles: number | null;
  windingConfigurationForRatedVoltage: string | null;
  shaftPowerKw: number | null;
  shaftPowerHp: number | null;
  serviceFactorSf: number | null;
  ratedSpeedRpm: number | null;
  ratedCurrentA: number | null;
  startingCurrentLockedRotorCurrentPercent: number | null;
  ratedTorqueNm: number | null;
  startingTorqueLockedRotorTorquePercent: number | null;
  cosPhi: number | null;
  fullLoadEfficiency: number | null;
  approval: string | null;
  windingInsulationClass: string | null;
  windingTempRise: string | null;
  constructionForm: string | null;
  enclosureRating: string | null;
  rotationSenseDriveEnd: string | null;
  biDirectional: boolean | null;
  electricalDocumentNumber: string | null;
  refMotor: string | null;
  altitudeAboveSealevelM: number | null;
  typeOfBearing: string | null;
  bearingDe: string | null;
  bearingNde: string | null;
  bearingAxialFixed: string | null;
  bearingInsulated: string | null;
  bearingDatasheet: string | null;
  greasingRequired: boolean | null;
  greasingInstructionDrawingNumber: string | null;
  greaseOrOilType: string | null;
  greasingDataSheet: string | null;
  quantityDriveEndG: number | null;
  greasingIntervalDriveEndH: number | null;
  quantityNonDriveEndG: number | null;
  greasingIntervalNonDriveEndH: number | null;
  deliveredConfiguration: string | null;
  positionGreasingNipplesDrawingNumber: string | null;
  positionGreaseReleaseDrawingNumber: string | null;
  positionConnectionsForcedLubricatedSleeveBearings: string | null;
  positionGreasingDataPlateDrawingNumber: string | null;
  drawingTemplateGreasingDataPlateDrawingNumber: string | null;
  maxOperatingLifetimeBearingsL: number | null;
  maxOperatingLifetimeBearingsHours: number | null;
  maxOperatingLifetimeWindingsL: number | null;
  maxOperatingLifetimeWindingsHours: number | null;
  maxOperatingLifetimeMotorL: number | null;
  maxOperatingLifetimeMotorHours: number | null;
  typeTestTemperatureRise: string | null;
  gearData: string | null;
  gearRatio: number | null;
  groundingBrush: boolean | null;
  groundingBrushMake: string | null;
  groundingBrushType: string | null;
  groundingBrushLifetime: number | null;
  groundingBrushDrawing: string | null;
  coolingFan: string | null;
  internalCoolingFanType: string | null;
  fanDrawingNo: string | null;
  fanCoverDrawingNo: string | null;
  heatExchangerMotorMounted: boolean | null;
  heatExchangerDatasheet: string | null;
  heatExchangerDimensionDrawingNo: string | null;
  heatExchangerDataplatePosition: string | null;
  heatExchangerDataplateDrawingTemplate: string | null;
  coolingMediumTemperatureMinC: number | null;
  coolingMediumTemperatureMaxC: number | null;
  altitudeMin: number | null;
  altitudeMaxM: number | null;
  maximumHumidityAbsoluteGm3: number | null;
  maximumHumidityRelativePercent: number | null;
  maximumHumidityTemperatureC: number | null;
  ambientTemperature: number | null;
  typeOfCooling: string | null;
  coolingMedium: string | null;
  coolingTemperatureC: number | null;
  externalCoolingFlowM3s: number | null;
  coolingDesignPressureBarE: number | null;
  internalCoolingFlowM3s: number | null;
  mainDimension3d: string | null;
  mainDimensionDrawingNo: string | null;
  noiseLevelCurveOrDatasheet: string | null;
  datasheetVibration: string | null;
  powerDeratingAmbientTempDrawingNo: string | null;
  powerDeratingAltitudeDrawingNo: string | null;
  torqueSpeedCurveDrawingNo: string | null;
  currentSpeedCurveDrawingNo: string | null;
  thermalLimitCurveDrawingNo: string | null;
  efficiencyDocumentSupplier: string | null;
  equivalentElectricalDrawingNo: string | null;
  fieldWeakeningCurvesDrawingNo: string | null;
  shaftKeyDimensionsDrawingNo: string | null;
  flangeGearFeetDrawingNo: string | null;
  positionConnectionDimensionsCooling: string | null;
  centerOfGravity: string | null;
  positionOfLiftingEye: string | null;
  positionDataplate: string | null;
  dataplateDrawing: string | null;
  partialLoad25Eff: number | null;
  partialLoad25Pf: number | null;
  partialLoad25Current: number | null;
  partialLoad25Speed: number | null;
  partialLoad50Eff: number | null;
  partialLoad50Pf: number | null;
  partialLoad50Current: number | null;
  partialLoad50Speed: number | null;
  partialLoad75Eff: number | null;
  partialLoad75Pf: number | null;
  partialLoad75Current: number | null;
  partialLoad75Speed: number | null;
  partialLoad100Eff: number | null;
  partialLoad100Pf: number | null;
  partialLoad100Current: number | null;
  partialLoad100Speed: number | null;
  partialLoadSf: number | null;
  partialLoadSfEff: number | null;
  partialLoadSfPf: number | null;
  partialLoadSfCurrent: number | null;
  partialLoadSfSpeed: number | null;
  efficiencyDataListFormat: string | null;
  dutyCycle: string | null;
  minFrequencyVsd: number | null;
  maxFrequencyVsd: number | null;
  operatingCondition: string | null;
  gridVoltage: number | null;
  gridFrequency: number | null;
  equivalentCircuitType: string | null;
  equivalentCircuitR1: number | null;
  equivalentCircuitX1: number | null;
  equivalentCircuitR2Accent: number | null;
  equivalentCircuitX2Accent: number | null;
  equivalentCircuitRm: number | null;
  equivalentCircuitXm: number | null;
  equivalentCircuitTdo: number | null;
  equivalentCircuitTd: number | null;
  equivalentCircuitTda: number | null;
  equivalentCircuitZbase: number | null;
  equivalentCircuitXr: number | null;
  equivalentCircuitRs: number | null;
  equivalentCircuitXs: number | null;
  equivalentCircuitX2: number | null;
  conduitMulticoreCableMain: string | null;
  conduitPowerCableQuantityMain: number | null;
  conduitPowerCableSizeMain: string | null;
  conduitPowerCableConnectionMain: string | null;
  conduitPowerCableLengthMmMain: number | null;
  conduitCableEmcProtectedMain: boolean | null;
  supplyLeadsQuantityMain: number | null;
  supplyLeadsSizeMm2Main: number | null;
  supplyLeadsCablelugMain: string | null;
  supplyLeadsLengthMmMain: number | null;
  earthingLeadsQuantityMain: number | null;
  earthingLeadsSizeMm2Main: number | null;
  earthingLeadsCablelugMain: string | null;
  earthingLeadsLengthMmMain: number | null;
  specificationSheetDetailedDataMain: string | null;
  conduitCableAuxControlsType1Aux1: string | null;
  conduitPowerCableQuantityAux1: number | null;
  conduitPowerCableSizeAux1: string | null;
  conduitPowerCableConnectionAux1: string | null;
  conduitPowerCableLengthMmAux1: number | null;
  conduitCableEmcProtectedAux1: boolean | null;
  supplyLeadsQuantityAux1: number | null;
  supplyLeadsSizeMm2Aux1: number | null;
  supplyLeadsCablelugAux1: string | null;
  supplyLeadsLengthMmAux1: number | null;
  conduitCableAuxControlsType2Aux2: string | null;
  conduitPowerCableQuantityAux2: number | null;
  conduitPowerCableSizeAux2: string | null;
  conduitPowerCableConnectionAux2: string | null;
  conduitPowerCableLengthMmAux2: number | null;
  conduitCableEmcProtectedAux2: boolean | null;
  supplyLeadsQuantityAux2: number | null;
  supplyLeadsSizeMm2Aux2: number | null;
  supplyLeadsCablelugAux2: string | null;
  supplyLeadsLengthMmAux2: number | null;
  specificationSheetDetailedDataAdditionalControlLeads: string | null;
  instructionBookGeneralDrawingNo: string | null;
  transportInstructionDrawingNo: string | null;
  shortLongTermStorageInstructionDrawingNo: string | null;
  alignmentLimitationsDrawingNo: string | null;
  mountingRequirementsDrawingNo: string | null;
  instructionForHeatExchangerDrawingNo: string | null;
  typeTestUnVoltageTolerance: string | null;
  nemaDesign: string | null;
  massKg: number | null;
  centreOfGravity: string | null;
  momentOfInertia: number | null;
  massOfRotorOrGearAssemblyKg: number | null;
  resonanceFrequency: number | null;
  torsionalStiffness: number | null;
  statorHousingMaterial: string | null;
  rotorMaterial: string | null;
  balancingAccordingEn60034_14: string | null;
  balanceLevel: string | null;
  deliveredWithKey: boolean | null;
  externalCoolingFlow: number | null;
  unitOfCoolingFlow: string | null;
  externalCoolingVentilatedFlangeDrawingNo: string | null;
  externalCoolingFlowDirection: string | null;
  externalCoolingMinFreeSpaceAirCoolingInletMm: number | null;
  externalCoolingMinFreeSpaceAirCoolingOutletMm: number | null;
  typeTestNoiseLevels: string | null;
  typeTestVibration: string | null;
  supportArrangement: string | null;
  currentTransmitter: boolean | null;
  currentTransmitterType: string | null;
  thermalTimeConstants100: number | null;
  thermalTimeConstants80: number | null;
  lockedRotorTimeCold: number | null;
  lockedRotorTimeWarm: number | null;
  timeConstantWarmingUpLoaded: number | null;
  timeConstantCoolingDownStandstill: number | null;
  severityClass: string | null;
  voltagePeak: number | null;
  maxDvdt: number | null;
  maxRiseTimeUs: number | null;
  voltageFreqVariationRefDoc: string | null;
  maxVoltageVariationNeg: number | null;
  maxVoltageVariationPos: number | null;
  maxFreqVariationNeg: number | null;
  maxFreqVariationPos: number | null;
  maxVoltageUnbalance: number | null;
  maxCurrentUnbalance: number | null;
  numberOfStartsReferenceDocNo: string | null;
  permissiveNumberOfStartsPerDay: number | null;
  permissiveNumberOfStartsPerHour: number | null;
  consecutiveStartsColdConditionAtUn: number | null;
  consecutiveStartsColdConditionAt80PercentUn: number | null;
  consecutiveStartsWarmConditionAtUn: number | null;
  consecutiveStartsWarmConditionAt80PercentUn: number | null;
  powerDeratingCoolantTemp: string | null;
  maxPermissibleThrustShockForcesAxial: number | null;
  datasheetShockForces: string | null;
  motorOptionsOther: string | null;
  freeComments1: string | null;
  freeComments2: string | null;
  freeComments3: string | null;
  freeComments4: string | null;
  freeComments5: string | null;
  noiseLevel: number | null;
  vibrationLevelLoadMmS: number | null;
  vibrationLevelUnloadMmS: number | null;
  vibrationLevelOnTorqueAmplitude: number | null;
  vibrationLevelOnTorqueFrequency: number | null;
  paintingColour: string | null;
  codeLetterLockedRotorKva: string | null;
  nemaNamePlate: string | null;
  windingProtection: boolean | null;
  windingProtectionWarningValue: number | null;
  windingProtectionShutdownValue: number | null;
  bearingProtection: boolean | null;
  bearingProtectionWarningValue: number | null;
  bearingProtectionShutdownValue: number | null;
  spmNippelsIncluded: boolean | null;
  spmNippelsPositionDrawingNo: string | null;
  spmNippelsWarningValue: number | null;
  spmNippelsShutdownValue: number | null;
  antiCondensationHeater: boolean | null;
  heaterSizeNumberOfPhases: number | null;
  heaterSizeVoltage: number | null;
  heaterSizePower: number | null;
  leakageWaterSensor: boolean | null;
  leakageWaterSensorType: string | null;
  currentTransmitterTerminalBox: string | null;
  starConnectionTerminalBox: string | null;
  currentTransmitterStarTerminalDrawing: string | null;
  currentTransmitterStarTerminalNumber: string | null;
  currentTransmitterSize: string | null;
  currentTransmitterConnectionType: string | null;
  currentTransmitterFlanged: boolean | null;
  currentTransmitterEmc: boolean | null;
  mainTerminalBox: string | null;
  mainTerminalBoxElectricalConnections: string | null;
  mainTerminalBoxDimensions: string | null;
  mainTerminalBoxSupplyCableEntryNumber: number | null;
  mainTerminalBoxSupplyCableEntrySize: string | null;
  mainTerminalBoxAuxCableEntryNumber: number | null;
  mainTerminalBoxAuxCableEntrySize: string | null;
  mainTerminalBoxFlangedRotatable: boolean | null;
  mainTerminalBoxEmcProtected: boolean | null;
  earthing: boolean | null;
  earthingOutsideBoltSize: string | null;
  earthingMaxCableSize: string | null;
  auxiliaryTerminalBox: string | null;
  auxiliaryTerminalBoxElectricalConnections: string | null;
  auxiliaryTerminalBoxDimensions: string | null;
  auxiliaryTerminalBoxCableInletOpenings1Number: number | null;
  auxiliaryTerminalBoxCableInletOpenings1Size: string | null;
  auxiliaryTerminalBoxCableInletOpenings2Number: number | null;
  auxiliaryTerminalBoxCableInletOpenings2Size: string | null;
  auxiliaryTerminalBoxEmcProtected: boolean | null;
  createdAtUtc: string | null;
  createdBy: string | null;
  updatedAtUtc: string | null;
  updatedBy: string | null;
}

export interface MechanicalData {
  id: number;
  motorInvestigationFlowId: number;
  motorInvestigationFlowAlternativeId: number | null;
  isInvestigationInternal: boolean;
  isInvestigationInProgress: boolean;
  isOldMotorData: boolean;
  acMotorNumber: string | null;
  mechanicalDrawingNumber: string | null;
  mainDimension3d: string | null;
  motorMountingTypeMasterId: number | null;
  frameSize: string | null;
  shaftDiameterMm: number | null;
  shaftLengthMm: number | null;
  keyLengthMm: number | null;
  keyWidthMm: number | null;
  shaftHeightMm: number | null;
  shaftToGbHousingDistanceMm: number | null;
  centerHeightOfGbMm: number | null;
  gbMountingPositionsMm: string | null;
  flangePcdMm: number | null;
  flangePcdHolesAngle: number | null;
  flangePcdHolesDiameterMmPerMetricThread: string | null;
  numberOfHolesOnFlangePcd: number | null;
  overallLengthMm: number | null;
  overallHeightMm: number | null;
  supportBoltPositionMm: string | null;
  supportBoltSizeMetricThreadOrMm: string | null;
  distanceBetweenSupportBoltSideView: number | null;
  distanceBetweenSupportBoltFrontView: number | null;
  baffleDiameterMm: number | null;
  mainTerminalBoxAngle: number | null;
  gearboxHousing: number | null;
  createdAtUtc: string | null;
  createdBy: string | null;
  updatedAtUtc: string | null;
  updatedBy: string | null;
}

interface MotorData {
  electricalData: ElectricalData;
  mechanicalData: MechanicalData;
}

export type { MotorData };

export interface MotorInvestigationComparisonApiResponse {
  oldMotor: MotorData;
  newMotor: MotorData;
}

export interface FetchReportPdfComparisonDataParams {
  oldMotorId: string;
  newMotorId: string;
}

const BASE_URL = process.env.REACT_APP_MOTER_SERVICE_API_BASE_URL as string;

if (!BASE_URL) {
  throw new Error('REACT_APP_MOTER_SERVICE_API_BASE_URL environment variable is not set');
}

const baseQuery = createBackendBaseQuery(BASE_URL);

export const motorInvestigationComparisonApiSlice = createApi({
  reducerPath: 'motorInvestigationComparisonApi',
  baseQuery: baseQuery,
  tagTypes: ['ComparisonData', 'ElectricalComparisonData', 'MechanicalComparisonData','ReportComparisonData'],
  endpoints(builders) {
    return {
      fetchComparisonData: builders.query<MotorInvestigationComparisonApiResponse, number>({
        query: (id) => ({
          url: `${API_URLS.MOTOR_INVESTIGATION.FETCH_INVESTIGATION_COMPARISON_DATA}?investigationFlowAlternativeId=${id}`,
          method: 'GET',
        }),
        providesTags: ['ComparisonData'],
        transformErrorResponse: transformApiError,
      }),
       fetchReportComparisonData: builders.query<MotorInvestigationComparisonApiResponse, number>({
        query: (id) => ({
          url: `${API_URLS.MOTOR_INVESTIGATION.FETCH_REPORT_COMPARISON_DATA}?investigationFlowAlternativeId=${id}`,
          method: 'GET',
        }),
        providesTags: ['ReportComparisonData'],
        transformErrorResponse: transformApiError,
      }),
      fetchReportPdfComparisonData: builders.query<
        MotorInvestigationComparisonApiResponse,
        FetchReportPdfComparisonDataParams
      >({
        query: ({ oldMotorId, newMotorId }) => ({
          url: `${API_URLS.MOTOR_INVESTIGATION.FETCH_REPORT_PDF_COMPARISON_DATA}?oldMotorId=${oldMotorId}&newMotorId=${newMotorId}`,
          method: 'GET',
        }),
        providesTags: ['ReportComparisonData'],
        transformErrorResponse: transformApiError,
      }),
      postElectricalData: builders.mutation<number, { ElectricalComparisonData: ElectricalData }>({
        query({ ElectricalComparisonData }) {
          return {
            url: API_URLS.MOTOR_INVESTIGATION.SAVE_ELECTRICAL_DATA,
            method: 'POST',
            body: ElectricalComparisonData,
          };
        },
        invalidatesTags: ['ElectricalComparisonData', 'ComparisonData', 'ReportComparisonData'],
        transformErrorResponse: transformApiError,
      }),

      postMechanicalData: builders.mutation<number, { MechanicalComparisonData: MechanicalData }>({
        query({ MechanicalComparisonData }) {
          return {
            url: API_URLS.MOTOR_INVESTIGATION.SAVE_MECHANICAL_DATA,
            method: 'POST',
            body: MechanicalComparisonData,
          };
        },
        invalidatesTags: ['MechanicalComparisonData', 'ComparisonData', 'ReportComparisonData'],
        transformErrorResponse: transformApiError,
      }),

      patchElectricalData: builders.mutation<
        { id: number; status: number },
        { id: number; patchData: PatchOperation[] }
      >({
        query({ id, patchData }) {
          return {
            url: `${API_URLS.MOTOR_INVESTIGATION.SAVE_ELECTRICAL_DATA}?id=${id}`,
            method: 'PATCH',
            body: patchData,
            headers: {
              'Content-Type': 'application/json-patch+json',
              Accept: 'application/json',
            },
            prepareHeaders: (headers: Headers) => {
              headers.set('Content-Type', 'application/json-patch+json');
              return headers;
            },
          };
        },
        invalidatesTags: ['ElectricalComparisonData', 'ComparisonData', 'ReportComparisonData'],
        transformErrorResponse: transformApiError,
      }),

      patchMechanicalData: builders.mutation<
        { id: number; status: number },
        { id: number; patchData: PatchOperation[] }
      >({
        query({ id, patchData }) {
          return {
            url: `${API_URLS.MOTOR_INVESTIGATION.SAVE_MECHANICAL_DATA}?id=${id}`,
            method: 'PATCH',
            body: patchData,
            headers: {
              'Content-Type': 'application/json-patch+json',
              Accept: 'application/json',
            },
            prepareHeaders: (headers: Headers) => {
              headers.set('Content-Type', 'application/json-patch+json');
              return headers;
            },
          };
        },
        invalidatesTags: ['MechanicalComparisonData', 'ComparisonData', 'ReportComparisonData'],
        transformErrorResponse: transformApiError,
      }),
      fetchMotorData: builders.query<MotorData, string>({
        query: (id) => ({
          url: `${API_URLS.MOTOR_SERVICE.MOTOR_DATA}?id=${id}`,
          method: 'GET',
        }),
        transformErrorResponse: transformApiError,
      }),
    };
  },
});

export const {
  useFetchComparisonDataQuery,
  useLazyFetchComparisonDataQuery,
  usePostElectricalDataMutation,
  usePatchElectricalDataMutation,
  usePostMechanicalDataMutation,
  usePatchMechanicalDataMutation,
  useFetchReportComparisonDataQuery,
  useLazyFetchReportComparisonDataQuery,
  useFetchReportPdfComparisonDataQuery,
  useLazyFetchReportPdfComparisonDataQuery,
  useFetchMotorDataQuery,
  useLazyFetchMotorDataQuery,
} = motorInvestigationComparisonApiSlice;
